import cn from "classnames";

// components
import { Alert, Icon } from "@trussworks/react-uswds";
import DisplayValue from "../DisplayValue";

// store
import { useSearchStore } from "store/searchStore";

// icons
import {
  UserIcon,
  AtIcon,
  PhoneIcon,
  FaxIcon,
  PinIcon,
  WorldIcon,
  StoreIcon,
  TimerOutline,
} from "../../icons";

// utils
import { refineCertificationTags } from "utils/helper";
import { getCapabilitiesNarrativeLink } from "utils/helper";
import { formatUnixTimestamp, formatAddress } from "utils/helper";

// styles
import styles from "./ProfileOverviewSection.module.scss";

export default function ProfileOverviewSection({
  isPrivileged,
}: {
  isPrivileged: boolean;
}) {
  const { business_profile } = useSearchStore();

  // const capabilitiesLink = getCapabilitiesNarrativeLink(business_profile);
  const underProtest = business_profile?.size_protest;

  const hideEmailInformation = !business_profile?.display_email;
  const hidePhoneInformation = !business_profile?.display_phone;
  const hideFaxInformation = !business_profile?.display_fax;
  const hideAddrInformation = !business_profile?.display_address;

  return (
    <>
      {underProtest && (
        <div className={styles["profile-alert-wrapper"]}>
          <Alert
            type="warning"
            heading="SBA has found this company to not be “small”"
            headingLevel="h6"
            className={styles["alert-wrapper"]}
          >
            The NAICS codes table shows the affected NAICS codes from the SBA’s
            size determination. If you own this business, resolve this by
            contacting the Small Business Administration or updating your SAM
            registration.
          </Alert>
        </div>
      )}
      <div className={styles["profile-overview-section-root"]}>
        <h2 className={styles["heading"]} id="profile-overview" tabIndex={-1}>
          Profile overview
        </h2>

        <h6 className={styles["last-update"]}>
          This profile was last updated:{" "}
          <DisplayValue
            value={business_profile?.last_update_date}
            renderer={(_value) => {
              return <>{formatUnixTimestamp(_value)}</>;
            }}
          />
        </h6>

        <h6 className={styles["sam-status"]}>
          SAM.gov registration status:{" "}
          <DisplayValue
            value={business_profile?.sam_extract_code}
            renderer={(_value) => {
              return (
                <>
                  {_value === "A"
                    ? "Active"
                    : _value === "E"
                    ? "Expired"
                    : "Other"}
                </>
              );
            }}
          />
        </h6>

        <div className={cn(styles["content-group"], styles["mt-6"])}>
          <h5 className={styles["label"]}>Capabilities narrative</h5>
          <DisplayValue
            messageElement="p"
            className={styles["value"]}
            value={business_profile?.capabilities_narrative}
          />
        </div>

        <div className={cn(styles["content-group"], styles["mt-6"])}>
          <h5 className={styles["label"]}>Capabilities statement link</h5>
          <DisplayValue
            className={styles["value"]}
            messageElement="p"
            value={business_profile?.capabilities_link}
            renderer={(_value) => (
              <>
                <a
                  href={business_profile?.capabilities_link}
                  target="_blank"
                  rel="noreferrer"
                  className="usa-link usa-link--external"
                  aria-label="Open capabilities statement in new tab"
                >
                  Capabilities statement
                </a>
              </>
            )}
          />
        </div>

        <div className={styles["content-section"]}>
          <div className="space-y-4 ">
            <div className={styles["data-field"]}>
              <UserIcon aria-label="Person icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Contact person</h5>
                <DisplayValue
                  messageElement="p"
                  className={styles["value"]}
                  value={business_profile?.contact_person}
                  renderer={(_value) => {
                    return <>{_value}</>;
                  }}
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <AtIcon aria-label="Email icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Email address</h5>
                {(!hideEmailInformation || isPrivileged) && (
                  <DisplayValue
                    messageElement="p"
                    className={styles["value"]}
                    value={business_profile?.email}
                    renderer={(_value) => (
                      <>
                        {_value === "Not Provided" ? (
                          _value
                        ) : (
                          <a
                            href={`mailto:${business_profile?.email}`}
                            className="usa-link usa-link"
                          >
                            {_value}
                          </a>
                        )}
                      </>
                    )}
                  />
                )}

                {hideEmailInformation && (
                  <div className="global__hide-information">
                    <Icon.Error aria-label="explanation icon" />
                    <p>
                      The business owner has hidden this information from public
                      searches
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["data-field"]}>
              <PhoneIcon aria-label="Phone icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Phone number</h5>

                {(!hidePhoneInformation || isPrivileged) && (
                  <DisplayValue
                    messageElement="p"
                    className={styles["value"]}
                    value={business_profile?.phone}
                    renderer={(_value) => {
                      const formatPhoneNumber = (phone) => {
                        if (!phone) return "";
                        return `${phone.slice(0, 3)}-${phone.slice(
                          3,
                          6
                        )}-${phone.slice(6)}`;
                      };
                      return <>{formatPhoneNumber(_value)}</>;
                    }}
                  />
                )}

                {hidePhoneInformation && (
                  <div className="global__hide-information">
                    <Icon.Error aria-label="explanation icon" />
                    <p>
                      The business owner has hidden this information from public
                      searches
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["data-field"]}>
              <FaxIcon aria-label="Fax icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Fax number</h5>

                {(!hideFaxInformation || isPrivileged) && (
                  <DisplayValue
                    messageElement="p"
                    className={styles["value"]}
                    value={business_profile?.fax}
                    renderer={(_value) => {
                      const formatFax = (fax) => {
                        if (!fax) return "";
                        return `${fax.slice(0, 3)}-${fax.slice(
                          3,
                          6
                        )}-${fax.slice(6)}`;
                      };
                      return <>{formatFax(_value)}</>;
                    }}
                  />
                )}

                {hideFaxInformation && (
                  <div className="global__hide-information">
                    <Icon.Error aria-label="explanation icon" />
                    <p>
                      The business owner has hidden this information from public
                      searches
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["data-field"]}>
              <PinIcon aria-label="Address icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Address</h5>

                {(!hideAddrInformation || isPrivileged) && (
                  <DisplayValue
                    messageElement="p"
                    className={styles["value"]}
                    value={formatAddress(business_profile)}
                    renderer={(_value) => {
                      return <>{_value}</>;
                    }}
                  />
                )}

                {hideAddrInformation && (
                  <div className="global__hide-information">
                    <Icon.Error aria-label="explanation icon" />
                    <p>
                      The business owner has hidden this information from public
                      searches
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["data-field"]}>
              <WorldIcon aria-label="Website icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Website</h5>
                <DisplayValue
                  className={styles["value"]}
                  messageElement="p"
                  value={business_profile?.website}
                  renderer={(_value) => {
                    let website = _value;
                    if (website.indexOf("http") === -1) {
                      website = "https://" + website;
                    }
                    return (
                      <>
                        <a
                          href={website}
                          target="_blank"
                          rel="noreferrer"
                          className="usa-link usa-link--external"
                        >
                          {_value}
                        </a>
                      </>
                    );
                  }}
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <StoreIcon aria-label="Store icon" />
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Additional website</h5>
                <DisplayValue
                  messageElement="p"
                  className={styles["value"]}
                  value={business_profile?.additional_website}
                  renderer={(_value) => {
                    let url = _value;
                    if (url.indexOf("http") === -1) {
                      url = "https://" + url;
                    }

                    return (
                      <>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="usa-link usa-link--external"
                        >
                          {url}
                        </a>
                      </>
                    );
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 ">
            <div className={styles["data-field"]}>
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>
                  Trade name (“Doing business as...”)
                </h5>
                <DisplayValue
                  messageElement="p"
                  className={styles["value"]}
                  value={business_profile?.dba_name}
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Year established</h5>
                <DisplayValue
                  messageElement="p"
                  className={styles["value"]}
                  value={business_profile?.year_established}
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>County code (3 digits)</h5>
                <DisplayValue
                  messageElement="p"
                  message="County code not returned"
                  className={styles["value"]}
                  value={business_profile?.county_code}
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>Congressional district</h5>
                <DisplayValue
                  messageElement="p"
                  className={styles["value"]}
                  value={business_profile?.congressional_district}
                  message="Unknown"
                />
              </div>
            </div>

            <div className={styles["data-field"]}>
              <div className={styles["group"]}>
                <h5 className={styles["label"]}>
                  Metropolitan statistical area
                </h5>
                <DisplayValue
                  messageElement="p"
                  message="MSA not returned"
                  className={styles["value"]}
                  value={business_profile?.msa}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
